import React, { useRef, useState } from 'react';
import { UploadCloud, FileCheck } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const UploadZone: React.FC = () => {
  const { uploadFile, selectedFileName, status } = useApp();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFile = (file?: File) => {
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      alert(`Error: File size cannot exceed ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <div
        className={`upload-zone ${isDragOver ? 'drag-over' : ''} ${status.loading ? 'disabled' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !status.loading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              handleFile(e.target.files[0]);
            }
          }}
        />

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <UploadCloud className="h-10 w-10 text-amber-500" style={{ marginBottom: '4px' }} />
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.95rem' }}>
            Drag &amp; drop PDF here or
          </p>
          <span className="file-label">Browse Files</span>
          <p style={{ margin: '8px 0 0 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Max file size: {MAX_FILE_SIZE_MB}MB
          </p>
        </div>
      </div>

      {selectedFileName && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'var(--primary)',
            fontWeight: 600,
            fontSize: '0.95rem',
            marginBottom: '12px',
          }}
        >
          <FileCheck className="h-4 w-4" />
          <span>Selected: {selectedFileName}</span>
        </div>
      )}

      {status.message && (
        <div className={`status-banner status-${status.type}`}>
          {status.loading && <div className="spinner" style={{ width: '16px', height: '16px' }} />}
          <span>{status.message}</span>
        </div>
      )}
    </div>
  );
};
